/* MARCA | 06/05/2026 | Client-side recently viewed products tab for tabbed-collection-list section */
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {{ id: string, handle: string, title: string, vendor: string, featured_image: string, price: number, compare_at_price: number | null }} ShopifyProduct
 * @typedef {{ id: string, handle: string, badge?: { text: string, bg: string, color: string } | null }} RecentlyViewedEntry
 */

(function () {
  var PANEL_SELECTOR = '[data-recently-viewed-panel]';
  var TAB_SELECTOR = '[data-recently-viewed-tab]';
  var CONTENT_SELECTOR = '[data-rv-content]';

  /** @type {Record<string, ShopifyProduct[]>} */
  var CACHE = {};

  function init() {
    /** @type {NodeListOf<HTMLElement>} */
    var panels = document.querySelectorAll(PANEL_SELECTOR);
    panels.forEach(function (panel) {
      var section = panel.closest('.shopify-section');
      if (!section) return;

      /** @type {HTMLElement | null} */
      var tab = section.querySelector(TAB_SELECTOR);
      if (!tab) return;

      var products = RecentlyViewed.getProducts();
      /** @type {RecentlyViewedEntry[]} */
      var validProducts = products.filter(function (/** @type {RecentlyViewedEntry} */ p) { return p.handle; });

      if (validProducts.length === 0) {
        tab.hidden = true;
        tab.style.display = 'none';
        panel.hidden = true;
        return;
      }

      tab.hidden = false;
      tab.style.display = '';

      var maxItems = parseInt(tab.getAttribute('data-rv-max') || '4', 10) || 4;
      /** @type {RecentlyViewedEntry[]} */
      var limitedProducts = validProducts.slice(0, maxItems);

      tab.addEventListener('click', function onFirstClick() {
        if (tab) tab.removeEventListener('click', onFirstClick);
        loadRecentlyViewed(panel, limitedProducts);
      });

      if (tab.getAttribute('aria-selected') === 'true') {
        loadRecentlyViewed(panel, limitedProducts);
      }
    });
  }

  /**
   * @param {HTMLElement} panel
   * @param {RecentlyViewedEntry[]} products
   */
  function loadRecentlyViewed(panel, products) {
    var cardMode = panel.getAttribute('data-card-mode') || 'full';

    if (cardMode === 'full') {
      loadRecentlyViewedFull(panel, products);
    } else {
      loadRecentlyViewedSimple(panel, products);
    }
  }

  /* MARCA | 27/05/2026 | Full card mode - fetches server-rendered product cards via Section Rendering API */
  /** @type {Record<string, string>} */
  var FULL_CARD_CACHE = {};
  var RV_SECTION_ID = 'rv-full-product-card';

  /**
   * @param {HTMLElement} panel
   * @param {RecentlyViewedEntry[]} products
   */
  function loadRecentlyViewedFull(panel, products) {
    /** @type {HTMLElement | null} */
    var contentEl = panel.querySelector(CONTENT_SELECTOR);
    /** @type {HTMLElement | null} */
    var loadingEl = panel.querySelector('.rv-panel__loading');

    if (!contentEl) return;

    var cacheKey = 'full:' + products.map(function (/** @type {RecentlyViewedEntry} */ p) { return p.handle; }).join(',');
    var cached = CACHE[cacheKey];
    if (cached) {
      renderFullContent(contentEl, loadingEl, /** @type {any} */ (cached), panel);
      return;
    }

    /** @type {Promise<string | null>[]} */
    var fetches = products.map(function (/** @type {RecentlyViewedEntry} */ p) {
      if (FULL_CARD_CACHE[p.handle]) {
        return Promise.resolve(/** @type {string | null} */ (FULL_CARD_CACHE[p.handle]));
      }
      return fetch('/products/' + p.handle + '?sections=' + RV_SECTION_ID)
        .then(function (/** @type {Response} */ res) {
          if (!res.ok) throw new Error('Section render failed: ' + p.handle);
          return res.json();
        })
        .then(function (/** @type {Record<string, string>} */ json) {
          var html = json[RV_SECTION_ID] || '';
          FULL_CARD_CACHE[p.handle] = html;
          return html;
        })
        .catch(function () { return null; });
    });

    Promise.all(fetches).then(function (results) {
      /** @type {string[]} */
      var validCards = /** @type {string[]} */ (results.filter(function (/** @type {string | null} */ r) { return r && r.trim().length > 0; }));
      if (validCards.length === 0) {
        if (loadingEl) loadingEl.hidden = true;
        if (contentEl) {
          contentEl.innerHTML = '<p class="rv-panel__empty">' + getEmptyMessage() + '</p>';
          contentEl.hidden = false;
        }
        return;
      }

      CACHE[cacheKey] = /** @type {any} */ (validCards);
      if (contentEl) renderFullContent(contentEl, loadingEl, validCards, panel);
    });
  }

  /**
   * @param {HTMLElement} contentEl
   * @param {HTMLElement | null} loadingEl
   * @param {string[]} cardHtmlArray
   * @param {HTMLElement} panel
   */
  function renderFullContent(contentEl, loadingEl, cardHtmlArray, panel) {
    var columns = parseInt(panel.getAttribute('data-columns') || '4', 10) || 4;
    var columnsGap = parseInt(panel.getAttribute('data-columns-gap') || '8', 10) || 8;
    var layoutType = panel.getAttribute('data-layout-type') || 'carousel';
    var sectionWidth = panel.getAttribute('data-section-width') || 'page-width';
    var mobileCardSize = panel.getAttribute('data-mobile-card-size') || '60cqw';

    /** @type {string[]} */
    var cards = cardHtmlArray.map(function (/** @type {string} */ html) {
      var wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      var cardEl = wrapper.querySelector('.rv-full-card__wrapper');
      return '<div class="resource-list__item">' + (cardEl ? cardEl.innerHTML : html) + '</div>';
    });

    var html = '';
    if (layoutType === 'carousel') {
      html = buildCarouselLayout(cards, columns, columnsGap, sectionWidth, mobileCardSize);
    } else {
      html = buildGridLayout(cards, columns, columnsGap);
    }

    contentEl.innerHTML = html;
    if (loadingEl) loadingEl.hidden = true;
    contentEl.hidden = false;

    window.dispatchEvent(new Event('resize'));

    /* MARCA | 27/05/2026 | Trigger Wishlist King refresh so it detects newly injected product cards */
    try {
      var wk = /** @type {any} */ (window)['WishlistKing'];
      if (wk && typeof wk.toolkit === 'object' && typeof wk.toolkit.refresh === 'function') {
        wk.toolkit.refresh();
      }
    } catch (e) { /* silently ignore if app not present */ }
  }

  /* MARCA | 27/05/2026 | Simple card mode - original lightweight client-rendered cards (preserved as fallback) */
  /**
   * @param {HTMLElement} panel
   * @param {RecentlyViewedEntry[]} products
   */
  function loadRecentlyViewedSimple(panel, products) {
    /** @type {HTMLElement | null} */
    var contentEl = panel.querySelector(CONTENT_SELECTOR);
    /** @type {HTMLElement | null} */
    var loadingEl = panel.querySelector('.rv-panel__loading');

    if (!contentEl) return;

    var cacheKey = products.map(function (/** @type {RecentlyViewedEntry} */ p) { return p.handle; }).join(',');
    var cached = CACHE[cacheKey];
    if (cached) {
      renderContent(contentEl, loadingEl, cached, panel);
      return;
    }

    /** @type {Promise<ShopifyProduct | null>[]} */
    var fetches = products.map(function (/** @type {RecentlyViewedEntry} */ p) {
      return fetch('/products/' + p.handle + '.js')
        .then(function (/** @type {Response} */ res) {
          if (!res.ok) throw new Error('Product not found: ' + p.handle);
          return /** @type {Promise<ShopifyProduct>} */ (res.json());
        })
        .catch(function () { return null; });
    });

    Promise.all(fetches).then(function (results) {
      /** @type {ShopifyProduct[]} */
      var validResults = /** @type {ShopifyProduct[]} */ (results.filter(Boolean));
      if (validResults.length === 0) {
        if (loadingEl) loadingEl.hidden = true;
        if (contentEl) {
          contentEl.innerHTML = '<p class="rv-panel__empty">' + getEmptyMessage() + '</p>';
          contentEl.hidden = false;
        }
        return;
      }

      /* MARCA | 26/05/2026 | Attach badge data from localStorage entries to fetched product objects */
      validResults.forEach(function (/** @type {ShopifyProduct} */ product) {
        var entry = products.find(function (/** @type {RecentlyViewedEntry} */ e) { return e.handle === product.handle; });
        if (entry && entry.badge) {
          /** @type {any} */ (product)._badge = entry.badge;
        }
      });

      CACHE[cacheKey] = validResults;
      if (contentEl) renderContent(contentEl, loadingEl, validResults, panel);
    });
  }

  /**
   * @param {HTMLElement} contentEl
   * @param {HTMLElement | null} loadingEl
   * @param {ShopifyProduct[]} products
   * @param {HTMLElement} panel
   */
  function renderContent(contentEl, loadingEl, products, panel) {
    var moneyFormat = panel.getAttribute('data-money-format') || '{{amount}}';
    var columns = parseInt(panel.getAttribute('data-columns') || '4', 10) || 4;
    var columnsGap = parseInt(panel.getAttribute('data-columns-gap') || '8', 10) || 8;
    var layoutType = panel.getAttribute('data-layout-type') || 'carousel';
    var sectionWidth = panel.getAttribute('data-section-width') || 'page-width';
    var mobileCardSize = panel.getAttribute('data-mobile-card-size') || '60cqw';

    /** @type {string[]} */
    var cardsHtml = products.map(function (/** @type {ShopifyProduct} */ product) {
      return buildProductCard(product, moneyFormat);
    });

    var html = '';
    if (layoutType === 'carousel') {
      html = buildCarouselLayout(cardsHtml, columns, columnsGap, sectionWidth, mobileCardSize);
    } else {
      html = buildGridLayout(cardsHtml, columns, columnsGap);
    }

    contentEl.innerHTML = html;
    if (loadingEl) loadingEl.hidden = true;
    contentEl.hidden = false;

    window.dispatchEvent(new Event('resize'));
  }

  /**
   * @param {ShopifyProduct} product
   * @param {string} moneyFormat
   * @returns {string}
   */
  function buildProductCard(product, moneyFormat) {
    var image = product.featured_image;
    var imageUrl = image ? getSizedImageUrl(image, 600) : "";
    var imageAlt = escapeHtml(product.title);
    var title = escapeHtml(product.title);
    var vendor = escapeHtml(product.vendor || "");
    var url = "/products/" + product.handle;
    var priceHtml = buildPriceHtml(product, moneyFormat);

    /* MARCA | 26/05/2026 | Render custom badge overlay on product image if badge data exists */
    var badge = /** @type {any} */ (product)._badge;
    var badgeHtml = "";
    if (badge && badge.text) {
      badgeHtml =
        '<div class="rv-product-card__badge" style="' +
        "--custom-badge-bg: " +
        escapeHtml(badge.bg || "#ebc7c9") +
        ";" +
        "--custom-badge-color: " +
        escapeHtml(badge.color || "var(--color-foreground)") +
        ";" +
        '">' +
        '<span class="rv-product-card__badge-label">' +
        escapeHtml(badge.text) +
        "</span>" +
        "</div>";
    }

    /* MARCA | 15/06/2026 | Percentage saving badge – shows 'X% Off' when compare_at_price exceeds price */
    var savingBadgeHtml = "";
    if (product.compare_at_price && product.compare_at_price > product.price) {
      var savingPercent = Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100);
      savingBadgeHtml =
        '<div class="rv-product-card__saving-badge">' +
        '<span class="rv-product-card__saving-badge-label">' +
        savingPercent + '% Off' +
        '</span></div>';
    }

    return (
      '<div class="resource-list__item">' +
      '<a href="' +
      url +
      '" class="rv-product-card color-scheme-3">' +
      '<div class="rv-product-card__media">' +
      (imageUrl
        ? '<img src="' +
          imageUrl +
          '" alt="' +
          imageAlt +
          '" loading="lazy" class="rv-product-card__img">'
        : '<div class="rv-product-card__placeholder"></div>') +
      badgeHtml +
      savingBadgeHtml +
      "</div>" +
      '<div class="rv-product-card__info">' +
      (vendor ? '<p class="rv-product-card__vendor">' + vendor + "</p>" : "") +
      '<p class="rv-product-card__title">' +
      title +
      "</p>" +
      priceHtml +
      "</div>" +
      "</a>" +
      "</div>"
    );
  }

  /**
   * @param {ShopifyProduct} product
   * @param {string} moneyFormat
   * @returns {string}
   */
  function buildPriceHtml(product, moneyFormat) {
    var price = product.price;
    var compareAtPrice = product.compare_at_price;
    var priceFormatted = formatMoney(price, moneyFormat, Shopify.currency.active);

    if (compareAtPrice && compareAtPrice > price) {
      var compareFormatted = formatMoney(compareAtPrice, moneyFormat, Shopify.currency.active);
      return (
        '<div class="rv-product-card__price rv-product-card__price--on-sale">' +
          '<span class="rv-product-card__price-current">' + priceFormatted + '</span>' +
          '<span class="rv-product-card__price-compare">' + compareFormatted + ' RRP</span>' +
        '</div>'
      );
    }

    return '<div class="rv-product-card__price">' + priceFormatted + '</div>';
  }

  /**
   * @param {string[]} cards
   * @param {number} columns
   * @param {number} gap
   * @param {string} sectionWidth
   * @param {string} mobileCardSize
   * @returns {string}
   */
  function buildCarouselLayout(cards, columns, gap, sectionWidth, mobileCardSize) {
    var styles =
      '--resource-list-column-gap-desktop: ' + gap + 'px;' +
      '--column-count: ' + columns + ';' +
      '--column-count-mobile: 1;' +
      '--mobile-card-size: ' + mobileCardSize + ';';

    var gutterStyle = sectionWidth === 'page-width'
      ? '--gutter-slide-width: var(--util-page-margin-offset);'
      : '--gutter-slide-width: 0px;';

    var guttersAttr = sectionWidth === 'page-width' ? ' gutters="start end"' : '';

    /* MARCA | 27/05/2026 | Build timeline scope for slideshow-component and add required ref/aria attrs for slide navigation */
    /** @type {string[]} */
    var timelineScopes = [];
    var slidesHtml = cards.map(function (/** @type {string} */ card, /** @type {number} */ i) {
      timelineScopes.push('--slide-' + i);
      return (
        '<slideshow-slide ref="slides[]" class="resource-list__slide" aria-hidden="' + (i === 0 ? 'false' : 'true') + '" style="--slideshow-timeline: --slide-' + i + ';">' +
          card +
        '</slideshow-slide>'
      );
    }).join('');

    var componentStyle = '--slideshow-timeline: ' + timelineScopes.join(', ') + ';';

    return (
      '<div class="resource-list resource-list__carousel force-full-width" style="' + styles + '">' +
        '<div style="' + gutterStyle + ' --slide-width-max: 450px;">' +
          '<slideshow-component class="resource-list__carousel" style="' + componentStyle + '" initial-slide="0">' +
            '<slideshow-container ref="slideshowContainer">' +
              '<slideshow-slides tabindex="-1" ref="scroller"' + guttersAttr + '>' +
                slidesHtml +
              '</slideshow-slides>' +
            '</slideshow-container>' +
          '</slideshow-component>' +
        '</div>' +
      '</div>'
    );
  }

  /**
   * @param {string[]} cards
   * @param {number} columns
   * @param {number} gap
   * @returns {string}
   */
  function buildGridLayout(cards, columns, gap) {
    var styles =
      '--resource-list-column-gap-desktop: ' + gap + 'px;' +
      '--resource-list-row-gap-desktop: ' + gap + 'px;' +
      '--resource-list-columns: repeat(' + columns + ', 1fr);' +
      '--resource-list-columns-mobile: repeat(2, 1fr);' +
      '--column-count-mobile: 2;';

    return (
      '<div class="resource-list resource-list--grid" style="' + styles + '">' +
        cards.join('') +
      '</div>'
    );
  }

  /**
   * Appends a width parameter to a Shopify CDN image URL.
   * @param {string} src
   * @param {number} width
   * @returns {string}
   */
  function getSizedImageUrl(src, width) {
    if (!src) return '';
    var separator = src.indexOf('?') !== -1 ? '&' : '?';
    return src + separator + 'width=' + width;
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /** @returns {string} */
  function getEmptyMessage() {
    return 'No recently viewed products';
  }

  init();

  document.addEventListener('shopify:section:load', function () {
    init();
  });
})();
